import Modal from '@oncolens/oncolens-ui/lib/components/Common/Modal';
import clsx from 'clsx';
import React, { useState } from 'react';
import { FaPencilAlt, FaTrash } from 'react-icons/fa';
import { RiskFactorForCreationDto, RiskFactorForUpdateDto } from '../dtos/RiskFactor';
import {
  useCreateRiskFactor,
  useDeleteRiskFactor,
  useUpdateRiskFactor
} from '../hooks/useRiskFactor';
import { RiskFactorForm } from './RiskFactorForm';
import { RiskFactorItem, useRiskFactorContext } from './RiskFactors';

interface RiskFactorProps {
	data: RiskFactorItem;
	episodeId: string;
}

const RiskFactor = ({ data, episodeId }: RiskFactorProps) => {
  const context = useRiskFactorContext();
  const { id } = data;
  const isEditing = context.activeIds.riskFactorId === id;
  const previouslySaved = Boolean(data.description);

  const [isApiActionExecuting, setApiActionExecuting] = useState(false);
  const createRiskFactorMutation = useCreateRiskFactor();
  const updateRiskFactorMutation = useUpdateRiskFactor();
  const deleteRiskFactorMutation = useDeleteRiskFactor();

  const handleApiUpdate = (formValues: RiskFactorForUpdateDto) => {
    setApiActionExecuting(true);

    updateRiskFactorMutation
      .mutateAsync({
        riskFactorId: id,
        data: formValues
      })
      .then(() => {
        context.setRiskFactors((riskFactors) => {
          const updatedValue = riskFactors.map((riskFactor) => {
            return riskFactor.id === id
              ? {
                id,
                ...formValues
							  }
              : riskFactor;
          });

          context.persistToForm(updatedValue);

          return updatedValue;
        });

        setApiActionExecuting(false);
        setInactive();
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleApiCreate = (formValues: RiskFactorForCreationDto) => {
    setApiActionExecuting(true);

    createRiskFactorMutation
      .mutateAsync({
        episodeId,
        data: [formValues]
      })
      .then((newData) => {
        context.setRiskFactors((riskFactors) => {
          const updatedValue = riskFactors.map((riskFactor) => {
            return riskFactor.id === id
              ? {
                ...newData[0],
                riskFactors
							  }
              : riskFactor;
          });

          context.persistToForm(updatedValue);

          return updatedValue;
        });

        setApiActionExecuting(false);
        setInactive();
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleApiDelete = () => {
    deleteRiskFactorMutation
      .mutateAsync(id)
      .then(() => {
        context.setRiskFactors((riskFactors) => {
          const updatedValue = riskFactors.filter((riskFactor) => riskFactor.id !== id);

          context.persistToForm(updatedValue);

          return updatedValue;
        });

        setApiActionExecuting(false);
        setInactive();
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleUpdate = (formValues: any) => {
    context.setRiskFactors((riskFactors) => {
      const updatedValue = riskFactors.map((riskFactor) =>
        riskFactor.id === id ? { ...riskFactor, ...formValues } : riskFactor
      );

      context.persistToForm(updatedValue);

      return updatedValue;
    });

    setInactive();
  };

  const handleCancel = () => {
    if (!previouslySaved) {
      handleRemove();
    } else {
      setInactive();
    }
  };

  const handleRemove = () => {
    context.setRiskFactors((riskFactors) => {
      const updatedValue = riskFactors.filter((riskFactor) => riskFactor.id !== id);

      context.persistToForm(updatedValue);

      return updatedValue;
    });
    setInactive();
  };

  const setActive = () => {
    context.setActiveIds((previous) => ({ ...previous, riskFactorId: id }));
  };

  const setInactive = () => {
    context.setActiveIds((previous) => ({ ...previous, riskFactorId: null }));
  };

  return (
    <div
      data-cy="risk-factor-container"
      className="relative mb-3 bg-white border border-gray-300 rounded-md shadow-md"
    >
      {isEditing ? (
        <div className="grid grid-cols-3 gap-3 p-3">
          <RiskFactorForm
            data={data}
            isInAutosave={context.isInAutosave}
            previouslySaved={previouslySaved}
            isApiActionExecuting={isApiActionExecuting}
            onCancel={handleCancel}
            onUpdate={handleUpdate}
            onApiCreate={handleApiCreate}
            onApiUpdate={handleApiUpdate}
          />
        </div>
      ) : (
        <div className="p-3 pb-4">
          <RiskFactorValues
            data={data}
            isInAutosave={context.isInAutosave}
            onSetActive={setActive}
            onDelete={handleApiDelete}
            onRemove={handleRemove}
          />
        </div>
      )}
      {
        <div
          className={clsx(
            'absolute top-0 left-0 w-full h-full bg-gray-300 opacity-0 pointer-events-none transition-opacity duration-300',
            Boolean(context.activeIds.riskFactorId) &&
							!isEditing &&
							'opacity-50 cursor-not-allowed pointer-events-auto'
          )}
        ></div>
      }
    </div>
  );
};

interface RiskFactorValuesProps {
	data: RiskFactorItem;
	isInAutosave: boolean;
	onSetActive: () => void;
	onDelete: () => void;
	onRemove: () => void;
}

const RiskFactorValues = ({
  data,
  isInAutosave,
  onSetActive,
  onDelete,
  onRemove
}: RiskFactorValuesProps) => (
  <>
    <div data-cy="risk-factor-values" className="flex space-x-4">
      <div className="w-36">
        <label htmlFor="discription" className="text-sm font-medium text-gray-800">
					Risk Factor
        </label>
        <p data-cy="risk-factor-values-description" className="text-sm">
          {data.description ?? '—'}
        </p>
      </div>
      <div className="w-24">
        <label htmlFor="value" className="text-sm font-semibold text-gray-800">
					Value
        </label>
        <p data-cy="risk-factor-values-value" className="text-sm">
          {data.value ?? '—'}
        </p>
      </div>
      <div className="flex-1 overflow-auto ">
        <label htmlFor="commnents" className="text-sm font-semibold text-gray-800">
					Comments
        </label>
        <p data-cy="risk-factor-values-comments" className="text-sm whitespace-pre-wrap">
          {data.comments ?? '—'}
        </p>
      </div>
      <div className="flex items-center ml-auto">
        <FaPencilAlt
          data-cy="risk-factors-edit-button"
          className="box-content p-2 mr-px text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
          onClick={onSetActive}
        />
        {isInAutosave ? (
          <Modal
            triggerElement={
              <FaTrash
                data-cy="risk-factors-delete-button"
                className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
              />
            }
          >
            <Modal.Header title="Delete Risk Factor" showClose={true} />
            <Modal.Content>
              <p>Are you sure you want to delete this risk factor?</p>
            </Modal.Content>
            <Modal.Buttons
              buttons={[
                {
                  label: 'Cancel',
                  style: 'secondary',
                  color: 'gray',
                  closeAfterClick: true,
                  className: 'mr-2'
                },
                {
                  label: 'Delete',
                  style: 'primary',
                  color: 'red',
                  closeAfterClick: true,
                  onClick: () => {
                    setTimeout(onDelete, 300);
                  }
                }
              ]}
            />
            {/* TODO: Remove set timeout. right now, the modal needs to live outside the removed part of the component but also still have a trigger in the right place
                      which means that clicking "Delete" removes the whole modal tree before the close animation can finish. */}
          </Modal>
        ) : (
          <FaTrash
            data-cy="risk-factors-delete-button"
            className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
            onClick={onRemove}
          />
        )}
      </div>
    </div>
  </>
);

export { RiskFactor as default };
